import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./AperturePaywall.module.scss"

/**
 * AperturePaywall (`/aperture`) — an original, self-contained ONBOARDING paywall
 * for Private Photo Vault Premium. Paywall type rolled at random this run:
 * ONBOARDING.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run is `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it — through an optical
 * metaphor: a lens that decides who is allowed to look.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "The lens only opens for you." A camera is the one thing that always says yes
 * — pick up any phone and its photos are right there for whoever is holding it,
 * for a shared album, for every app you've handed photo access. Private Photo
 * Vault puts an APERTURE in front of your memories: a lens that closes for
 * everyone and opens for exactly one person — you (Face ID / Touch ID / a code).
 * The onboarding truth, told with the camera's own iris.
 *
 * ── Interaction (the hero mechanic) ─────────────────────────────────────────
 * The hero is a real camera lens holding a montage of your memories, aperture
 * wide open. You SWEEP THE LENS RING (a radial drag around the barrel) and the
 * iris blades stop down — a metallic hexagonal aperture twists shut over the
 * photos — until the lens is closed and a single "opens for you" seal blooms at
 * the centre. Sweep back (or tap) to open it again and see who else could look.
 * A radial ring-sweep — not a tap-to-gather (Fold), a press-and-hold (Nightlatch),
 * a drag-key (Cipher), a flip (Flipside), a zip (Seam) or a dial-to-a-number.
 *
 * ── Divergence (vs. the 130+ prior concepts, incl. the security cluster) ────
 * Layout      : a single centred CAMERA LENS / optical barrel — bezel, focal
 *               ticks, iris — not a wall, door, folio, grid, keypad, orbiting
 *               exposure field (Fold) or frosted window.
 * Interaction : SWEEP-THE-RING radial drag that stops the aperture down. New to
 *               the set. Replayable; auto-demos once.
 * Metaphor    : a CAMERA'S OWN IRIS — the thing that lets light (and looks) in,
 *               now closing to one viewer. Photo-native, unmistakably a vault.
 * Value frame : RISK REVERSAL / who-is-allowed-to-look — "opens for anyone →
 *               opens for you." Not loss, urgency, scarcity or privacy-math.
 * Palette     : warm cinematic optical — charcoal barrel, amber lens glints,
 *               a cool "only-you" seal. A deliberate step away from Fold's light
 *               airy daylight field.
 * Register    : the quiet confidence of a good lens — precise, warm, in control.
 * CTA         : "Close the lens for good" — yearly featured.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Every "who can look" surface named is real (a phone's photos genuinely sit
 * open to whoever holds it, to shared albums, to cloud sync and to apps granted
 * photo access) and every safeguard named is a REAL Private Photo Vault feature
 * (only-you lock with Face ID / Touch ID / a code · encrypted Cloud Vault ·
 * break-in snapshots · app disguise). No countdown, no fabricated scarcity, NO
 * data-loss threat — nothing is deleted from anywhere; the lens only decides who
 * is allowed to look. "Save 79%" is exact arithmetic from live product data.
 * Yearly is the featured, default, most prominent price; monthly is small and
 * secondary. The 7-day free trial is the real trial. Renders on built-in debug
 * product data with bundled photos — no native injection, no external egress.
 */

// The memories behind the glass — bundled, self-contained repo photos. They fill
// the lens while it's open and get stopped down behind the iris as it closes.
const MEMORIES = [
  "/byheart/m02.jpg",
  "/byheart/m05.jpg",
  "/byheart/m08.jpg",
  "/byheart/m11.jpg",
  "/byheart/m03.jpg",
  "/byheart/m14.jpg",
]

// Who can look through an ordinary phone camera right now — real, everyday
// surfaces. Each is shut out one f-stop at a time as the aperture closes.
const ONLOOKERS = [
  "Anyone holding your phone",
  "A shared album",
  "Cloud photo sync",
  "Apps you gave photo access",
]

// What closing the lens actually turns on — real Private Photo Vault safeguards.
// Presented as a compact stop-grid, distinct from a plain feature list.
const STOPS = [
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Opens for you only",
    line: "Face ID, Touch ID or a private code",
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "A private backup, yours on every device",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-in snapshots",
    line: "A silent photo of a wrong-code attempt",
  },
  {
    key: "disguise",
    Icon: svg.featureIcons.Disguise,
    name: "App disguise",
    line: "Hide the vault behind an ordinary app",
  },
]

// ── aperture geometry ───────────────────────────────────────────────────────
const SIZE = 320 // svg viewbox (square)
const CX = SIZE / 2
const CY = SIZE / 2
const HOUSING_R = 150 // inner glass radius
const BLADES = 7 // iris blade count

const lerp = (a: number, b: number, t: number) => a + (b - a) * t
const easeInOut = (t: number) =>
  t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2

// Build the SVG path for the iris blades: the metal ring between the housing
// circle and the (shrinking, twisting) polygonal opening. Even-odd fill leaves
// the opening as a hole, so the metal covers more of the glass as it closes.
function irisPath(p: number): string {
  const e = easeInOut(p)
  const openR = lerp(HOUSING_R + 8, 5, e) // opening shrinks toward the centre
  const twist = (e * 52 * Math.PI) / 180 // characteristic aperture twist

  // outer housing circle, as two arcs (clockwise)
  let d =
    `M ${CX - HOUSING_R} ${CY} ` +
    `A ${HOUSING_R} ${HOUSING_R} 0 1 1 ${CX + HOUSING_R} ${CY} ` +
    `A ${HOUSING_R} ${HOUSING_R} 0 1 1 ${CX - HOUSING_R} ${CY} Z `

  // inner opening polygon (reverse winding via even-odd fill leaves the hole)
  for (let k = 0; k < BLADES; k++) {
    const a = twist + (k * 2 * Math.PI) / BLADES - Math.PI / 2
    const x = CX + openR * Math.cos(a)
    const y = CY + openR * Math.sin(a)
    d += `${k === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)} `
  }
  d += "Z"
  return d
}

// Blade seams — thin lines from each opening vertex out to the housing rim, so
// the closed iris reads as segmented metal rather than a solid disc.
function seamLines(p: number) {
  const e = easeInOut(p)
  const openR = lerp(HOUSING_R + 8, 5, e)
  const twist = (e * 52 * Math.PI) / 180
  const lines: { x1: number; y1: number; x2: number; y2: number }[] = []
  for (let k = 0; k < BLADES; k++) {
    const a = twist + (k * 2 * Math.PI) / BLADES - Math.PI / 2
    lines.push({
      x1: CX + openR * Math.cos(a),
      y1: CY + openR * Math.sin(a),
      x2: CX + (HOUSING_R + 2) * Math.cos(a),
      y2: CY + (HOUSING_R + 2) * Math.sin(a),
    })
  }
  return lines
}

const AperturePaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()

  // Blend the safe-area insets into the cinematic barrel so it runs edge to edge.
  useEffect(() => {
    actions.setHeaderInsetColor("#14161c")
    actions.setFooterInsetColor("#101218")
  }, [actions])

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── aperture state ─────────────────────────────────────────────────────────
  // g: 0 = wide open (anyone can look), 1 = stopped fully down (opens for you).
  // A spring chases a target; the ring-sweep and taps set that target.
  const [g, setG] = useState(0)
  const gRef = useRef(0)
  const targetRef = useRef(0)
  const raf = useRef<number | null>(null)
  const userActed = useRef(false)

  const closed = g > 0.8

  const tick = useCallback(() => {
    const target = targetRef.current
    const cur = gRef.current
    const k = target > cur ? 0.13 : 0.11
    let next = cur + (target - cur) * k
    if (Math.abs(target - next) < 0.0012) next = target
    gRef.current = next
    setG(next)
    if (next !== target) raf.current = requestAnimationFrame(tick)
    else raf.current = null
  }, [])

  const springTo = useCallback(
    (v: number) => {
      targetRef.current = v
      if (reduceMotion) {
        if (raf.current != null) cancelAnimationFrame(raf.current)
        raf.current = null
        gRef.current = v
        setG(v)
        return
      }
      if (raf.current == null) raf.current = requestAnimationFrame(tick)
    },
    [reduceMotion, tick],
  )

  const toggle = useCallback(() => {
    userActed.current = true
    springTo(targetRef.current > 0.5 ? 0 : 1)
  }, [springTo])

  // ── ring sweep ──────────────────────────────────────────────────────────────
  // Dragging around the barrel accumulates swept angle; ~200° of sweep stops the
  // aperture the whole way down. Release latches to the nearer end for a clean
  // resting state. Cancels the auto-demo/spring so the user is in control.
  const ringRef = useRef<HTMLDivElement | null>(null)
  const dragging = useRef(false)
  const lastAngle = useRef(0)
  const SWEEP_RANGE = 200 // degrees of sweep for a full close

  const angleAt = useCallback((clientX: number, clientY: number) => {
    const el = ringRef.current
    if (!el) return 0
    const r = el.getBoundingClientRect()
    const cx = r.left + r.width / 2
    const cy = r.top + r.height / 2
    return (Math.atan2(clientY - cy, clientX - cx) * 180) / Math.PI
  }, [])

  const onDown = useCallback(
    (e: React.PointerEvent) => {
      userActed.current = true
      dragging.current = true
      if (raf.current != null) {
        cancelAnimationFrame(raf.current)
        raf.current = null
      }
      gRef.current = g
      targetRef.current = g
      lastAngle.current = angleAt(e.clientX, e.clientY)
      try {
        ;(e.target as Element).setPointerCapture?.(e.pointerId)
      } catch {}
    },
    [angleAt, g],
  )

  const onMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging.current) return
      const a = angleAt(e.clientX, e.clientY)
      let delta = a - lastAngle.current
      if (delta > 180) delta -= 360
      if (delta < -180) delta += 360
      lastAngle.current = a
      // clockwise sweep (positive delta) closes the aperture
      let next = gRef.current + delta / SWEEP_RANGE
      next = Math.max(0, Math.min(1, next))
      gRef.current = next
      targetRef.current = next
      setG(next)
    },
    [angleAt],
  )

  const onUp = useCallback(() => {
    if (!dragging.current) return
    dragging.current = false
    springTo(gRef.current > 0.5 ? 1 : 0) // latch to the nearer resting state
  }, [springTo])

  // Auto-demo: hold the wide-open "anyone can look" state for a beat so the risk
  // reads, then stop the lens down so the payoff lands. Reduced-motion arrives
  // already closed (the calm end state).
  useEffect(() => {
    if (reduceMotion) {
      targetRef.current = 1
      gRef.current = 1
      setG(1)
      return
    }
    if (window.location.search.includes("nodemo")) return
    const t = window.setTimeout(() => {
      if (!userActed.current) springTo(1)
    }, 1100)
    return () => {
      window.clearTimeout(t)
      if (raf.current != null) cancelAnimationFrame(raf.current)
      raf.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  // How many everyday surfaces can still look through the lens (drops to 0 as it
  // closes) — honest, and it never claims anything is deleted.
  const openCount = Math.round(ONLOOKERS.length * (1 - g))

  // f-stop readout: purely decorative flavour that tracks the sweep (wide f/1.4
  // open → f/22 closed). Not a claim, just the lens speaking its own language.
  const fStop = useMemo(() => {
    const stops = [1.4, 2, 2.8, 4, 5.6, 8, 11, 16, 22]
    const i = Math.min(stops.length - 1, Math.round(g * (stops.length - 1)))
    return stops[i]
  }, [g])

  const d = irisPath(g)
  const seams = seamLines(g)

  return (
    <div
      className={`${styles.root} ${closed ? styles.isClosed : ""}`}
      style={{ ["--g" as any]: g }}
    >
      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.top}>
          <div className={styles.brand}>
            <span className={styles.brandMark} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="15" height="15">
                <circle
                  cx="12"
                  cy="12"
                  r="8.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
                <circle cx="12" cy="12" r="3" fill="currentColor" />
              </svg>
            </span>
            <span className={styles.brandName}>
              Private Photo Vault
              <span className={styles.brandTag}>Premium</span>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={() => native.postNativeEvent("close")}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* ── intro ──────────────────────────────────────────────────────── */}
        <section className={styles.intro}>
          <span className={styles.kicker}>
            <span className={styles.kickerDot} aria-hidden="true" />
            Welcome to Private Photo Vault
          </span>
          <h1 className={styles.title}>
            {closed ? (
              <>
                Now the lens
                <br />
                <span className={styles.accent}>opens for you.</span>
              </>
            ) : (
              <>
                Your camera
                <br />
                <span className={styles.accentWarn}>opens for anyone.</span>
              </>
            )}
          </h1>
          <p className={styles.sub}>
            {closed
              ? "Every memory sits behind a lens that opens for one person — you. No shared album, no sync, no app gets to look in."
              : "Pick up any phone and the photos are right there. Let's put a lens in front of yours that only you can open."}
          </p>
        </section>

        {/* ── the lens (hero) ────────────────────────────────────────────── */}
        <section className={styles.stage}>
          {/* live "who can look" readout */}
          <div
            className={`${styles.readout} ${closed ? styles.readoutClosed : ""}`}
            aria-live="polite"
          >
            <span className={styles.readoutDot} aria-hidden="true" />
            {closed ? (
              <>
                Opens for <b>only you</b>
              </>
            ) : (
              <>
                Open to{" "}
                <b>
                  {openCount} other{openCount === 1 ? "" : "s"}
                </b>
              </>
            )}
            <span className={styles.fstop} aria-hidden="true">
              f/{fStop}
            </span>
          </div>

          <div
            ref={ringRef}
            className={styles.lens}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
            role="button"
            tabIndex={0}
            aria-label={
              closed
                ? "Open the lens again to see who else could look"
                : "Sweep the ring to close the lens"
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") toggle()
            }}
          >
            {/* outer barrel + knurled focus ring */}
            <span className={styles.barrel} aria-hidden="true">
              {Array.from({ length: 48 }).map((_, i) => (
                <span
                  key={i}
                  className={styles.knurl}
                  style={{ ["--a" as any]: `${(i * 360) / 48}deg` }}
                />
              ))}
            </span>

            {/* glass: the memory montage, visible while the aperture is open */}
            <span className={styles.glass} aria-hidden="true">
              <span className={styles.montage}>
                {MEMORIES.map((src, i) => (
                  <span
                    key={src}
                    className={styles.mem}
                    style={{
                      backgroundImage: `url(${src})`,
                      ["--i" as any]: i,
                    }}
                  />
                ))}
              </span>
              <span className={styles.glassTint} />
              <span className={styles.glassGlint} />
            </span>

            {/* the iris blades */}
            <svg
              className={styles.iris}
              viewBox={`0 0 ${SIZE} ${SIZE}`}
              aria-hidden="true"
            >
              <defs>
                <radialGradient id="apBlade" cx="42%" cy="36%" r="75%">
                  <stop offset="0%" stopColor="#3a3f4b" />
                  <stop offset="55%" stopColor="#24272f" />
                  <stop offset="100%" stopColor="#15171d" />
                </radialGradient>
                <radialGradient id="apInner" cx="50%" cy="50%" r="60%">
                  <stop offset="0%" stopColor="#f6b45a" stopOpacity="0.0" />
                  <stop offset="82%" stopColor="#f6b45a" stopOpacity="0.0" />
                  <stop offset="100%" stopColor="#f6b45a" stopOpacity="0.5" />
                </radialGradient>
              </defs>
              {/* warm rim light on the still-open aperture */}
              <circle
                cx={CX}
                cy={CY}
                r={HOUSING_R}
                fill="url(#apInner)"
                opacity={1 - g}
              />
              {/* the closing metal */}
              <path d={d} fill="url(#apBlade)" fillRule="evenodd" />
              {/* blade seams */}
              <g
                stroke="#0e1015"
                strokeWidth="1.4"
                opacity={0.55 * easeInOut(g)}
              >
                {seams.map((l, i) => (
                  <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} />
                ))}
              </g>
              {/* soft specular edge where the blades meet the light */}
              <g
                stroke="#5b6270"
                strokeWidth="0.9"
                opacity={0.4 * easeInOut(g)}
                fill="none"
              >
                {seams.map((l, i) => (
                  <line
                    key={i}
                    x1={l.x1}
                    y1={l.y1}
                    x2={(l.x1 + l.x2) / 2}
                    y2={(l.y1 + l.y2) / 2}
                  />
                ))}
              </g>
              {/* housing rim */}
              <circle
                cx={CX}
                cy={CY}
                r={HOUSING_R}
                fill="none"
                stroke="#0c0e13"
                strokeWidth="3"
              />
            </svg>

            {/* the "only you" seal — blooms at the centre once fully stopped down */}
            <span className={styles.seal} aria-hidden="true">
              <span className={styles.sealRing} />
              <span className={styles.sealFace}>
                <svg viewBox="0 0 24 24" width="30" height="30">
                  <path
                    d="M4 8V6.5A2.5 2.5 0 0 1 6.5 4H8M16 4h1.5A2.5 2.5 0 0 1 20 6.5V8M20 16v1.5a2.5 2.5 0 0 1-2.5 2.5H16M8 20H6.5A2.5 2.5 0 0 1 4 17.5V16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                  <path
                    d="M9 9.5v1.2M15 9.5v1.2"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                  <path
                    d="M9.2 14.4s1.2 1.4 2.8 1.4 2.8-1.4 2.8-1.4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span className={styles.sealText}>Opens for you</span>
            </span>
          </div>

          {/* the one gesture */}
          <button
            type="button"
            className={styles.sweep}
            onClick={toggle}
            aria-pressed={closed}
          >
            <span className={styles.sweepIcon} aria-hidden="true">
              {closed ? (
                <svg viewBox="0 0 24 24" width="17" height="17">
                  <circle
                    cx="12"
                    cy="12"
                    r="8.4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                  />
                  <path
                    d="M12 3.6a8.4 8.4 0 0 1 6 2.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="17" height="17">
                  <circle
                    cx="12"
                    cy="12"
                    r="8.4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                  />
                  <path
                    d="M18 6a8.4 8.4 0 1 1-6-2.4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                  />
                  <path
                    d="M12 3.6L14.6 3M12 3.6l.4 2.6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
            <span className={styles.sweepLabel}>
              {closed ? "Open the lens again" : "Sweep the ring to close it"}
            </span>
          </button>
        </section>

        {/* ── honest framing ─────────────────────────────────────────────── */}
        <p className={styles.proof}>
          <span className={styles.proofMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13">
              <circle
                cx="12"
                cy="12"
                r="8.2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
              />
              <path
                d="M8.5 12.4l2.4 2.4 4.6-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Nothing leaves your phone and nothing gets deleted — the lens only
          decides who is allowed to look.
        </p>

        {/* ── who's shut out as it closes (honest, secondary) ────────────── */}
        <ul className={styles.onlookers}>
          {ONLOOKERS.map((o, i) => {
            const shut = g > (i + 0.5) / ONLOOKERS.length
            return (
              <li
                key={o}
                className={`${styles.onlooker} ${shut ? styles.shut : ""}`}
              >
                <span className={styles.onlookerEye} aria-hidden="true">
                  {shut ? (
                    <svg viewBox="0 0 24 24" width="13" height="13">
                      <path
                        d="M3 12s3.5-6 9-6 9 6 9 6M4.5 15.5l2-2.6M19.5 15.5l-2-2.6M9 17l.6-2.6M15 17l-.6-2.6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                      />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="13" height="13">
                      <path
                        d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12z"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                      />
                      <circle cx="12" cy="12" r="2.3" fill="currentColor" />
                    </svg>
                  )}
                </span>
                <span className={styles.onlookerName}>{o}</span>
                <span className={styles.onlookerState}>
                  {shut ? "shut out" : "can look"}
                </span>
              </li>
            )
          })}
        </ul>

        {/* ── what closing the lens turns on (real safeguards) ───────────── */}
        <div className={styles.stops}>
          <div className={styles.stopsHead}>
            <span>What closing the lens turns on</span>
            <span className={styles.stopsCount}>{STOPS.length} safeguards</span>
          </div>
          <div className={styles.stopsGrid}>
            {STOPS.map((s) => (
              <div key={s.key} className={styles.stop}>
                <span className={styles.stopIcon} aria-hidden="true">
                  <s.Icon width={17} height={17} />
                </span>
                <span className={styles.stopName}>{s.name}</span>
                <span className={styles.stopLine}>{s.line}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── pricing: yearly featured, monthly small & secondary ────────── */}
        <div className={styles.plans}>
          <button
            className={`${styles.yearly} ${
              plan === "yearly" ? styles.selected : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.yearlyGlow} aria-hidden="true" />
            <span className={styles.yearlyHead}>
              <span className={styles.yearlyName}>
                Premium · 1 year
                <span className={styles.trialPill}>
                  {trialDays}-day free trial
                </span>
              </span>
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.yearlyPriceRow}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>/ year</span>
              <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
              <span className={styles.check} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
            <span className={styles.yearlyFoot}>
              Every safeguard, on for a full year
            </span>
          </button>

          <button
            className={`${styles.monthly} ${
              plan === "monthly" ? styles.monthlySel : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span>Or monthly</span>
            <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
          </button>
        </div>

        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaLabel}>
            {plan === "yearly"
              ? `Close the lens for good — ${trialDays}-day free trial`
              : "Close the lens for good"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `Then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default AperturePaywall
